import { comluxgroup } from '@luxuryescapes/contract-data-event-schemas'
import { RouterState } from 'connected-react-router'
import moment from 'moment'
import { type SelfDescribingJson, setUserId } from '@snowplow/browser-tracker'

import { AppStore } from '../../store'
import config from 'constants/config'
import { CART_SCHEMA, PAGE_VIEW_CONTEXT_SCHEMA } from 'constants/analytics'
import { AnalyticsPage } from 'contexts/Analytics/analyticsPageContext'
import {
  buildInsuranceUpsellContext,
  createTourOfferItemContexts,
  experienceOfferEventsMap, getExperienceContext,
  getItemsContextForOffer,
  getItemsContextForCheckout, getPageOfferCategory,
  getItemsContextForOrder,
  offerIdStateHasError,
  mapToSnowplowFilters,
  buildSearchContext,
  buildLuxPlusUpsellContext,
} from './helpers'
import type {
  ExperienceEventDefinitionKey,
  ExperienceEventEntity,
  ExperienceOfferEventContext,
} from './helpers'
import { reportClientError } from 'services/errorReportingService'
import { GAImpressionFieldObject, GAProductFieldObject, SnowplowImpressionData } from 'analytics/eventDefinitions'
import { getPageCategoryAndType } from './pages'
import { allOptimizelyExperiments } from 'lib/optimizely/optimizelyUtils'
import { nonNullable } from 'lib/array/arrayUtils'
import { getCartId } from 'selectors/checkoutSelectors'
import orderContext from './eventContexts/orderContext'
import paymentContext from './eventContexts/paymentContext'
import { tripTabs } from 'tripPlanner/utils/tabs'
import { FlightsFareTypes } from 'constants/flight'
import { SearchEventData } from 'contexts/GlobalSearch/GlobalSearchTracking'
import { SearchAnalyticsLocation, SearchAnalyticsItemType } from 'analytics/mapSnowplowSearchTracking'

export type SnowplowItemContextType = 'flight' | 'experience' | 'cruise' | 'hotel' | 'insurance' | 'bookingProtection' | 'tour' | 'giftCard' | 'luxuryPlus' | 'carHire'

export interface BaseSnowplowEvent {
  context?: Array<SelfDescribingJson> | null;
  // Only use this if you want to explicitly determine where the context handler looks in redux to determine the items data
  // By default is is determined by looking at what page the user is on or what information is on the page which currently works for all but one case
  itemContextSource?: 'order' | 'checkout' | 'offer';
  itemContextTypesToSkip?: SnowplowItemContextType[];
}
export interface SelfDescribingSnowlplowEvent extends BaseSnowplowEvent {
  event: SelfDescribingJson
}
export interface PageviewSnowlplowEvent extends BaseSnowplowEvent {
  title: string;
}
export type SnowplowEvent = SelfDescribingSnowlplowEvent | PageviewSnowlplowEvent;

let _store: AppStore | undefined

export function registerCartTrackingStore(store: AppStore) {
  _store = store
}

/**
 * Event definitions
 */
interface SnowplowClientEventParams {
  subject: string,
  action: string,
  category: string,
  type: 'interaction' | 'nonInteraction' | 'operational',
  context?: SelfDescribingJson[],
  // itemContextTypesToSkip will not add the item to the context when the source is checkout
  itemContextTypesToSkip?: SnowplowItemContextType[],
  // 2 fields necessary if the client event is to make it to optimizely
  optimizelyEventId?: string,
  optimizelyEventKey?: string,
}
export function clientEvent(params: SnowplowClientEventParams): SnowplowEvent {
  if (params.optimizelyEventId && params.optimizelyEventKey) {
    params.context = [
      ...params.context || [],
      comluxgroup.createOptimizelyEventContext_1_0_0({
        id: params.optimizelyEventId,
        key: params.optimizelyEventKey,
      })]
  }

  return {
    context: params.context,
    itemContextTypesToSkip: params.itemContextTypesToSkip || [],
    event: comluxgroup.createClientEvent_1_0_0({
      subject: params.subject,
      action: params.action,
      type: params.type,
      category: params.category,
    }),
  }
}

export function impressionEvent(
  obj: GAImpressionFieldObject | GAProductFieldObject,
  listId: string,
  listName: string,
  currency: string,
  schema: string,
  listSource?: string,
): SnowplowEvent {
  const data: SnowplowImpressionData = {
    id: obj.id,
    name: obj.name,
    productType: obj.category,
    position: obj.position,
    currency,
    price: obj.price,
    listId,
    listName,
    listSource,
  }
  return {
    event: {
      schema,
      data,
    },
  }
}

export function luxPlusUpsellEvent(
  notSuitable: string,
  yes: string,
  whyNotSuitable?: string,
  isLuxPlusOffered?: string,
  declineReason?: string,
): SnowplowEvent {
  return {
    context: buildLuxPlusUpsellContext(
      notSuitable,
      yes,
      whyNotSuitable,
      isLuxPlusOffered,
      declineReason,
      _store ? _store.getState().auth.account.agentId : undefined,
    ),
    event: comluxgroup.createClientEvent_1_0_0({
      subject: 'luxplus_upsell_reminder',
      type: 'interaction',
      action: 'submit',
      category: 'luxplus',
    }),
  }
}

export function agentInsuranceUpsellEvent(
  notSuitable: string,
  yes: string,
  no: string,
  whyNotSuitable?: string,
  isCovermore?: string,
  isOffered?: string,
  declineReason?: string,
): SnowplowEvent {
  return {
    context: buildInsuranceUpsellContext(
      notSuitable,
      yes,
      no,
      whyNotSuitable,
      isCovermore,
      isOffered,
      declineReason,
      _store ? _store.getState().auth.account.agentId : undefined,
    ),
    event: comluxgroup.createClientEvent_1_0_0({
      subject: 'insurance_upsell_reminder',
      type: 'interaction',
      action: 'submit',
      category: 'checkout',
    }),
  }
}

export function experienceEvent(
  eventDefinitionKey: ExperienceEventDefinitionKey,
  geo: App.GeoState, eventsContexts?: Array<ExperienceEventEntity>,
): SnowplowEvent {
  const event = experienceOfferEventsMap[eventDefinitionKey]
  const eventsContextsWithGeo = eventsContexts?.map<ExperienceOfferEventContext>(eventContext => ({
    ...eventContext,
    data: {
      ...eventContext.data,
      geo,
    },
  })) ?? []
  return {
    context: eventsContextsWithGeo?.length ? getExperienceContext(eventsContextsWithGeo) : [],
    event: comluxgroup.createClientEvent_1_0_0({
      ...event,
      category: 'experiences_offer',
    }),
  }
}

export function uspAboutUsEvent(
  subject: string,
): SnowplowEvent {
  return {
    context: [],
    event: comluxgroup.createClientEvent_1_0_0({
      subject,
      action: 'clicked',
      category: 'usp_about_us',
      type: 'interaction',
    }),
  }
}

export type AuthState = 'started' | 'succeeded' | 'failed'

export function logOperational(subject: string, action: string): SnowplowEvent {
  return {
    context: [],
    event: comluxgroup.createClientEvent_1_0_0({
      action,
      subject,
      category: 'logging',
      type: 'operational',
    }),
  }
}

export function logRegistration(provider: App.AuthSource, data: string): SnowplowEvent {
  return {
    context: [],
    event: comluxgroup.createClientEvent_1_0_0({
      action: `${data}_succeeded`,
      subject: `${provider}_registration`,
      category: 'logging',
      type: 'operational',
    }),
  }
}

export function logAuth(provider: App.AuthSource, data: string): SnowplowEvent {
  return {
    context: [],
    event: comluxgroup.createClientEvent_1_0_0({
      action: `${data}_succeeded`,
      subject: `${provider}_auth`,
      category: 'logging',
      type: 'operational',
    }),
  }
}

export function offerPageHotLeadEvent(
  offer: App.Offer | App.BedbankOffer | Tours.V2Offer | App.CruiseOffer,
  lead: App.HotLeadOffer,
  geo: App.GeoState,
): SnowplowEvent | undefined {
  try {
    let contextItems
    const duration = lead.checkOut && lead.checkIn ? moment(lead.checkOut).diff(lead.checkIn, 'days') : undefined
    if (lead.occupancy.length) {
      contextItems = lead.occupancy.map(room => comluxgroup.createItem_1_0_0({
        brand: config.BRAND,
        currency: geo.currentCurrency,
        region: geo.currentRegionCode,
        offerId: offer.id,
        offerType: offer.type,
        travelStart: lead.checkIn,
        travelEnd: lead.checkOut,
        year: lead.year?.toString(),
        month: lead.month?.toString(),
        variant: lead.variant,
        duration,
        childrenAges: room.childrenAge,
        numberOfAdults: room.adults,
        numberOfChildren: room.children,
        numberOfInfants: room.infants,
      }))
    }
    else {
      contextItems = [comluxgroup.createItem_1_0_0({
        brand: config.BRAND,
        currency: geo.currentCurrency,
        region: geo.currentRegionCode,
        offerId: offer.id,
        offerType: offer.type,
        travelStart: lead.checkIn,
        travelEnd: lead.checkOut,
        year: lead.year?.toString(),
        month: lead.month?.toString(),
        variant: lead.variant,
        duration,
      })]
    }

    return {
      context: contextItems,
      event: comluxgroup.createClientEvent_1_0_0({
        subject: 'hot_lead',
        type: 'interaction',
        action: 'sent',
        category: 'offer_page',
      }),
    }
  } catch (e) {
    reportClientError(e)
  }
}

export function searchEventWithContext(
  contextLocation: SearchAnalyticsLocation,
  searchItemCategory: SearchAnalyticsItemType,
  snowplowSearchContextData: comluxgroup.SearchContext_1_0_0,
): SnowplowEvent {
  let placement
  let type
  if (contextLocation === 'search-list') {
    placement = 'main'
  } else if (contextLocation === 'common-search') {
    placement = 'header'
  }

  if (searchItemCategory === 'recent') {
    type = 'recent_search'
  } else if (searchItemCategory === 'popular') {
    type = 'trending_destination'
  } else {
    type = 'standard'
  }

  return {
    context: [
      comluxgroup.createSearchContext_1_0_0({
        ...snowplowSearchContextData,
      }),
    ],
    event: comluxgroup.createSearch_2_0_0({
      type,
      placement,
    }),
  }
}

export function searchEvent(subject: string, category: string, data: SearchEventData): SnowplowEvent {
  const { searchItem, checkIn, checkOut, vertical, isFlexibleDates, occupancies, filters } = data
  const baseContext = buildSearchContext()

  const formattedOccupancies = (occupancies ?? []).map(
    occupants => ({ numberOfChildren: (occupants?.infants ?? 0) + (occupants?.children ?? 0), numberOfAdults: occupants.adults }))

  return {
    context: [comluxgroup.createSearch_1_0_2({
      ...baseContext,
      searchType: searchItem?.searchType ?? '',
      verticals: [vertical],
      searchParams: {
        ...baseContext.searchParams,
        checkIn: checkIn ?? null,
        checkOut: checkOut ?? null,
        occupancies: formattedOccupancies,
        isFlexibleDates: Boolean(isFlexibleDates),
        searchText: searchItem?.format?.mainText ?? null,
        filters: filters ? mapToSnowplowFilters(filters) : {},
      },
    })],
    event: comluxgroup.createClientEvent_1_0_0({
      action: 'clicked',
      subject,
      type: 'interaction',
      category,
    }),
  }
}

function getConfirmationPageOrderId(routerState: RouterState): string | undefined {
  return routerState?.location?.query?.orderId
}

/**
 *
 * Use this function to add any global context to Snowplow events that are async
 * The function will be called right before each event is sent and awaited
 */
export async function asyncBaseContext(itemContextSource?: BaseSnowplowEvent['itemContextSource'], itemsToSkip: SnowplowItemContextType[] = []): Promise<SelfDescribingJson<Record<string, unknown>>[] | null | undefined> {
  if (!_store) {
    return []
  }

  const state: App.State = _store.getState()

  const routerState: RouterState = state.router

  // See if the page is part of the mapped analytics pages
  const pageData = getPageCategoryAndType(routerState.location.pathname)

  // Otherwise, see if it is an offer page
  const pageCategory = await getPageOfferCategory(_store.getState())
  if (!pageData.category && !pageData.type && pageCategory) {
    pageData.category = pageCategory
    pageData.type = 'offer'
  }

  const context: SelfDescribingJson<Record<string, unknown>>[] = []

  const promises: Promise<Array<SelfDescribingJson> | undefined>[] = []

  // Get items context just when it's an offer or checkout page
  const currentPath = state.routeHistory.currentPath
  const pathParams = state.routeHistory.currentPathParams as { orderId?: string, offerId?: string, id?: string, step?: string }
  const isCheckoutPage = pathParams.step && (currentPath.includes('checkout') || currentPath.includes('booking'))
  const offerId = pathParams.offerId || pathParams.id
  const isOfferPage = !!offerId
  const isOrderPage = !!(pathParams.orderId || getConfirmationPageOrderId(state.router))

  if (isCheckoutPage || itemContextSource === 'checkout') promises.push(getItemsContextForCheckout(state, itemsToSkip))
  else if (isOrderPage || itemContextSource === 'order') promises.push(getItemsContextForOrder(state))
  else if (isOfferPage || itemContextSource === 'offer') {
    const itemsContext = getItemsContextForOffer(state)
    if (itemsContext) context.push(...itemsContext)
    if (offerId && offerIdStateHasError(state, offerId)) {
      pageData.type = 'error'
    }
  }
  const promiseContexts = await Promise.all(promises)
  context.push(...nonNullable(promiseContexts.flat()))
  context.push(
    comluxgroup.createPage_1_0_0({
      type: pageData.type,
      category: pageData.category,
    }),
  )
  return context
}

export function baseContext(): SelfDescribingJson[] {
  if (!_store) {
    return []
  }

  const state = _store.getState()

  const authState: App.AuthState = state.auth
  const geoState: App.GeoState = state.geo

  const optimizelyExperimentData = allOptimizelyExperiments(state)

  const context = [
    comluxgroup.createBrand_1_0_0({
      brand: config.BRAND,
    }),
    comluxgroup.createRegion_1_0_0({
      region: geoState.currentRegionCode,
    }),
    comluxgroup.createLuxPlus_1_0_0({
      isMember: state.loyalty.member.subscription.item?.status === 'ACTIVE',
      tier: state.loyalty.member.subscription.item?.tier,
    }),
    comluxgroup.createExperimentContext_1_0_0({
      flags: optimizelyExperimentData,
    }),
  ]
  if (authState.account.memberId && authState.account.email) {
    setUserId(authState.account.memberId)
    context.push(comluxgroup.createUser_1_0_0({
      id: authState.account.memberId,
      email: authState.account.email,
      first_name: authState.account.givenName,
      is_spoofed: authState.account.isSpoofed,
    }))
  }
  return context
}

export function signupSuccessEvent(method: App.SignUpAuthSource): SnowplowEvent {
  return {
    event: comluxgroup.createSignupSuccess_1_0_0({
      method,
    }),
  }
}

export function signinSuccessEvent(method: App.AuthSource): SnowplowEvent {
  return {
    event: comluxgroup.createSigninSuccess_1_0_0({
      method,
    }),
  }
}

export function selectPackageUpgradeOptionToCart(
  offer: Tours.V2Offer,
  option: string,
  triggerLocation: AnalyticsPage,
  regionCode: string,
  currency: string,
  tourItems: App.Checkout.TourV2Item[],
): SnowplowEvent {
  return {
    context: createTourOfferItemContexts(offer, regionCode, currency, tourItems),
    event: comluxgroup.createClientEvent_1_0_0({
      subject: `tour_upgrade_${option.toLowerCase()}`,
      type: 'interaction',
      action: 'pressed',
      category: triggerLocation,
    }),
  }
}

export function tourTrendingDestination(country: string, action: string): SnowplowEvent {
  return {
    context: [],
    event: comluxgroup.createClientEvent_1_0_0({
      action,
      subject: `tour_trending_destinations_${country}`,
      category: 'vertical_page',
      type: 'interaction',
    }),
  }
}

export function tourExploreCategory(category: string): SnowplowEvent {
  return {
    context: [],
    event: comluxgroup.createClientEvent_1_0_0({
      action: 'pressed',
      subject: `tour_explore_category_${category}`,
      category: 'vertical_page',
      type: 'interaction',
    }),
  }
}

export function tourExploreOperator(operatorName: string): SnowplowEvent {
  return {
    context: [],
    event: comluxgroup.createClientEvent_1_0_0({
      action: 'pressed',
      subject: `tour_explore_operator - ${operatorName}`,
      category: 'vertical_page',
      type: 'interaction',
    }),
  }
}

export function tourCarouselHomepage(category: string): SnowplowEvent {
  return {
    context: [],
    event: comluxgroup.createClientEvent_1_0_0({
      action: 'view',
      subject: `offerlist-tourspage-${category}-carousel`,
      category: 'vertical_page',
      type: 'nonInteraction',
    }),
  }
}

export function clickViewAllCarouselExclusive(offerType: string): SnowplowEvent {
  return {
    context: [],
    event: comluxgroup.createClientEvent_1_0_0({
      action: 'pressed',
      subject: `topexclusive-carousel-${offerType}-viewall`,
      category: 'vertical_page',
      type: 'interaction',
    }),
  }
}

export function trackPageView(pathTemplate: string, pathParamsObject: any): SnowplowEvent {
  return {
    title: document.title,
    context: [{
      schema: PAGE_VIEW_CONTEXT_SCHEMA,
      data: {
        pathTemplate,
        pathParams: JSON.stringify(pathParamsObject),
      },
    }],
  }
}

export function purchaseSuccess(order: App.Order): SnowplowEvent | undefined {
  try {
    const state = _store?.getState()
    if (!state) { throw new Error('No redux registered for tracking purchase success snowplow events') }
    const cartId = getCartId(state)
    return {
      // event is triggered right after transitioning to purchase success page but before loading order details in state
      // so it needs to specifically get it from the checkout state
      itemContextSource: 'checkout',
      context: [
        comluxgroup.createCart_1_0_0({
          cartId,
        }),
        orderContext(order),
        ...paymentContext(order.payments),
      ],
      event: comluxgroup.createPurchaseSuccess_1_0_0({}),
    }
  } catch (e) {
    reportClientError(e)
  }
}

export function addToCart(): SnowplowEvent | undefined {
  try {
    if (!_store) {
      throw new Error('No redux registered for tracking cart snowplow events')
    }
    const state = _store.getState()
    const cartId = getCartId(state)
    return {
      itemContextSource: 'checkout',
      context: [{
        schema: CART_SCHEMA,
        data: {
          cartId,
        },
      }],
      event: comluxgroup.createAddToCart_1_0_0({}),
    }
  } catch (e) {
    reportClientError(e)
  }
}

export function switchTripPlannerTabEvent(tab: typeof tripTabs[number]['label']): SnowplowEvent {
  return {
    context: [],
    event: comluxgroup.createClientEvent_1_0_0({
      action: 'click',
      subject: `${tab.toLowerCase()}_tab`,
      category: AnalyticsPage.tripPlanner,
      type: 'interaction',
    }),
  }
}

export function switchExploreLocationEvent(): SnowplowEvent {
  return {
    context: [],
    event: comluxgroup.createClientEvent_1_0_0({
      action: 'click',
      subject: 'change_explore_location',
      category: AnalyticsPage.tripPlanner,
      type: 'interaction',
    }),
  }
}

export function switchExploreCategoryEvent(category: string): SnowplowEvent {
  return {
    context: [],
    event: comluxgroup.createClientEvent_1_0_0({
      action: 'click',
      subject: `change_explore_category_${category}`,
      category: AnalyticsPage.tripPlanner,
      type: 'interaction',
    }),
  }
}

export function saveExploreItemEvent(): SnowplowEvent {
  return {
    context: [],
    event: comluxgroup.createClientEvent_1_0_0({
      action: 'click',
      subject: 'save_explore_item',
      category: AnalyticsPage.tripPlanner,
      type: 'interaction',
    }),
  }
}

export function addExploreItemToItineraryEvent(): SnowplowEvent {
  return {
    context: [],
    event: comluxgroup.createClientEvent_1_0_0({
      action: 'click',
      subject: 'add_to_itinerary_explore_item',
      category: AnalyticsPage.tripPlanner,
      type: 'interaction',
    }),
  }
}

export function viewMoreDetailsExploreItemEvent(): SnowplowEvent {
  return {
    context: [],
    event: comluxgroup.createClientEvent_1_0_0({
      action: 'click',
      subject: 'view_more_details_explore_item',
      category: AnalyticsPage.tripPlanner,
      type: 'interaction',
    }),
  }
}

export function openMapSearchEvent(): SnowplowEvent {
  return {
    context: [],
    event: comluxgroup.createClientEvent_1_0_0({
      action: 'click',
      subject: 'open_map_search',
      category: AnalyticsPage.tripPlanner,
      type: 'interaction',
    }),
  }
}

export function clickMapSearchResultEvent(): SnowplowEvent {
  return {
    context: [],
    event: comluxgroup.createClientEvent_1_0_0({
      action: 'click',
      subject: 'map_search_result',
      category: AnalyticsPage.tripPlanner,
      type: 'interaction',
    }),
  }
}

export function saveMapSearchResultOnTileEvent(): SnowplowEvent {
  return {
    context: [],
    event: comluxgroup.createClientEvent_1_0_0({
      action: 'tile',
      subject: 'save_map_search_result',
      category: AnalyticsPage.tripPlanner,
      type: 'interaction',
    }),
  }
}

export function saveMapSearchResultInListEvent(): SnowplowEvent {
  return {
    context: [],
    event: comluxgroup.createClientEvent_1_0_0({
      action: 'result',
      subject: 'save_map_search_result',
      category: AnalyticsPage.tripPlanner,
      type: 'interaction',
    }),
  }
}

export function tripDocumentEvent(action: 'upload' | 'view'): SnowplowEvent {
  return {
    context: [],
    event: comluxgroup.createClientEvent_1_0_0({
      action,
      subject: 'trip_document',
      category: AnalyticsPage.tripPlanner,
      type: 'interaction',
    }),
  }
}

export function promotionViewEvent(name: string): SnowplowEvent {
  return {
    context: [comluxgroup.createPromotion_1_0_0({
      type: 'banner',
      name,
    })],
    event: comluxgroup.createPromotionView_1_0_0({}),
  }
}

export function promotionClickEvent(name: string): SnowplowEvent {
  return {
    context: [comluxgroup.createPromotion_1_0_0({
      type: 'banner',
      name,
    })],
    event: comluxgroup.createPromotionClick_1_0_0({}),
  }
}

export function openRedeemGiftCardEvent(page: AnalyticsPage): SnowplowEvent {
  return clientEvent({ subject: 'open_redeem_gift_card', action: 'clicked', category: page, type: 'interaction' })
}

export function scanGiftCardStartEvent(page: AnalyticsPage): SnowplowEvent {
  return clientEvent({ subject: 'scan_gift_card', action: 'start', category: page, type: 'interaction' })
}

export function scanGiftCardTakePhotoEvent(page: AnalyticsPage): SnowplowEvent {
  return clientEvent({ subject: 'scan_gift_card', action: 'take_photo', category: page, type: 'interaction' })
}

export function scanGiftCardReturnToManualEntryEvent(page: AnalyticsPage): SnowplowEvent {
  return clientEvent({ subject: 'scan_gift_card', action: 'enter_details_manually', category: page, type: 'interaction' })
}

export function scanGiftCardSuccessExactMatchEvent(page: AnalyticsPage): SnowplowEvent {
  return clientEvent({ subject: 'scan_gift_card', action: 'success_exact_match', category: page, type: 'nonInteraction' })
}

export function scanGiftCardSuccessModifiedEvent(page: AnalyticsPage): SnowplowEvent {
  return clientEvent({ subject: 'scan_gift_card', action: 'success_modified_from_scan', category: page, type: 'nonInteraction' })
}

export function clickSearchFormApplyButton(page: AnalyticsPage): SnowplowEvent {
  return clientEvent({ subject: 'search_apply_button', action: 'clicked', category: page, type: 'interaction' })
}

export function clickSearchFormSearchButton(page: AnalyticsPage): SnowplowEvent {
  return clientEvent({ subject: 'search_button', action: 'clicked', category: page, type: 'interaction' })
}

interface FlightItemSnowplowContext extends Omit<comluxgroup.Item_1_1_0, 'categoryId' | 'productId'>{
  originAirportCode: string;
  arrivalAirportCode: string;
  departureDate: string;
  arrivalDate?: string;
  pricePerAdult: number;
  airline: string;
  fareType: FlightsFareTypes
}

export function createFlightItemContext(data: FlightItemSnowplowContext): SelfDescribingJson {
  return comluxgroup.createItem_1_1_0({
    categoryId: 'flight',
    productId: 'flight',
    currency: data.currency,
    offerId: data.offerId,
    originCity: data.originCity,
    destinationCity: data.destinationCity,
    travelStart: data.departureDate,
    travelEnd: data.arrivalDate,
    duration: data.duration,
    numberOfAdults: data.numberOfAdults,
    numberOfChildren: data.numberOfChildren,
    price: data.pricePerAdult,
    originCountry: data.originCountry,
    destinationCountry: data.destinationCountry,
    metadata: JSON.stringify({
      airline: data.airline,
      originAirportCode: data.originAirportCode,
      arrivalAirportCode: data.arrivalAirportCode,
    }),
    typeId: data.fareType,
  })
}
